import { useEffect, useMemo, useRef, useState } from "react";
import { api, type Dashboard, type Filter } from "../api";

/**
 * Find-a-customer search: pick what to search (customer, salesperson…), then
 * type to narrow or open the list to see everything. Choosing one filters the
 * whole dashboard to it.
 *
 * The searchable fields come from the config's dimensions, so a new client that
 * can be broken down by, say, region automatically gets a region search with no
 * code change.
 */
export function Search({
  dashboard,
  onSelect,
}: {
  dashboard: Dashboard;
  onSelect: (filter: Filter) => void;
}) {
  const dims = dashboard.dimensions;
  const [field, setField] = useState(dims[0]?.field ?? "");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<string[]>([]);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  // Load every value for the chosen field once, then filter in the browser as
  // the user types — instant, and a customer list is small enough to hold.
  useEffect(() => {
    if (!field) return;
    api
      .dimensionValues(dashboard.dataset, field)
      .then(setValues)
      .catch(() => setValues([]));
    setQuery("");
  }, [dashboard.dataset, field]);

  // Close the dropdown when clicking away.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? values.filter((v) => v.toLowerCase().includes(q)) : values;
    return list.slice(0, 50);
  }, [values, query]);

  const dimLabel = dims.find((d) => d.field === field)?.label ?? "value";

  function choose(value: string) {
    onSelect({ field, op: "eq", value, label: dimLabel });
    setQuery("");
    setOpen(false);
  }

  if (dims.length === 0) return null;

  return (
    <div className="search" ref={boxRef}>
      <select
        className="search-field"
        value={field}
        onChange={(e) => setField(e.target.value)}
        aria-label="What to search"
      >
        {dims.map((d) => (
          <option key={d.field} value={d.field}>
            {d.label}
          </option>
        ))}
      </select>

      <div className="search-input-wrap">
        <span className="search-icon" aria-hidden="true">
          🔍
        </span>
        <input
          className="search-input"
          type="text"
          value={query}
          placeholder={`Search ${dimLabel.toLowerCase()}…`}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") setHighlight((h) => Math.min(h + 1, matches.length - 1));
            else if (e.key === "ArrowUp") setHighlight((h) => Math.max(h - 1, 0));
            else if (e.key === "Enter" && matches[highlight]) choose(matches[highlight]);
            else if (e.key === "Escape") setOpen(false);
          }}
        />
      </div>

      {open && (
        <div className="search-menu">
          {matches.length === 0 ? (
            <div className="search-empty">No matches</div>
          ) : (
            matches.map((v, i) => (
              <button
                key={v}
                className={`search-option${i === highlight ? " active" : ""}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => choose(v)}
              >
                {v}
              </button>
            ))
          )}
          {values.length > matches.length && (
            <div className="search-more">Keep typing to narrow {values.length} results…</div>
          )}
        </div>
      )}
    </div>
  );
}
